import React, { ReactNode, Suspense } from 'react';
import { Flex } from 'glints-aries/es';
import { Card, Icon, Typography } from 'glints-aries/es/@next';
import { Green, Neutral } from 'glints-aries/es/@next/utilities/colors';
import {
  space4,
  space16,
  space24,
} from 'glints-aries/es/@next/utilities/spacing';
import { useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ASSET_TYPES,
  CompanyStatus,
  COUNTRY_CODES,
} from '../../../common/constants';
import { useGetSessionCompany } from '../../../common/hooks/useGetSessionCompany';
import useIsLegalDocsAutoVerifyEnabled from '../../../common/hooks/useIsLegalDocsAutoVerifyEnabled';
import { VerificationMethod } from '../../../common/interfaces/entities';
import { CTAText } from '../../CustomSpan';
import useDocumentTutorialModal from '../DocumentTutorialModal/useDocumentTutorialModal';
import CompanyLegalNameField from './components/CompanyLegalNameField';
import { CompanyNPWPNumberField } from './components/CompanyNpwpNumberField';
import FileUploader from './components/FileUploader';
import TextField from './components/TextField';
import {
  companyNPWPTooltip,
  DefaultFileUploadHelperText,
  requiredFieldsMessages,
} from './constants';
import {
  useCompanyVerificationFormContext,
  useIsViewOnlyMode,
} from './FormContext';
import {
  NibTypes,
  useCompanyVerificationTracking,
  VERIFICATION_COMPANY_NIB_NUMBER_FILLED,
} from './hooks/useCompanyVerificationTracking';
import useValidateImageQuality from './hooks/useValidateImageQuality';
import { LegalDocLabelByCountryCode } from './legalDocumentLabels';
import { FormContentStyles } from './styles.sc';
import VerificationAdditionalDocumentsUploader from './VerificationAdditionalDocumentsUploader';
import VerificationPopover from './VerificationPopover';

type Props = {
  VerificationBanner: React.ReactNode;
  disabled?: boolean;
};

function VerifyWithLegalDocumentsForm({ VerificationBanner, disabled }: Props) {
  const { openModal: openTutorialModal, modal: DocumentTutorialModal } =
    useDocumentTutorialModal();
  const { setValue, control } = useCompanyVerificationFormContext();
  const allowCompanyNameEdit = useWatch({
    control,
    name: 'verifyWithLegalDocuments.allowCompanyNameEdit',
  });
  const { sessionCompany } = useGetSessionCompany();
  const isCompanyVerified = sessionCompany?.status === CompanyStatus.VERIFIED;

  const isViewOnlyMode = useIsViewOnlyMode();
  const [companyNPWPDoc, legalDocumentNotOfficial] = useWatch({
    control,
    name: [
      'verifyWithLegalDocuments.companyNPWPDoc',
      'verifyWithLegalDocuments.legalDocumentNotOfficial',
    ],
  });
  const showNPWPNumberField =
    Boolean(companyNPWPDoc) && legalDocumentNotOfficial === true;
  const { formatMessage } = useIntl();

  const validateImageQuality = useValidateImageQuality();

  const { trackOnFieldValid } = useCompanyVerificationTracking();
  const companyNIBNumberFieldName = 'verifyWithLegalDocuments.companyNIBNumber';
  const companyNibNumberOnBlur = trackOnFieldValid(
    companyNIBNumberFieldName,
    VERIFICATION_COMPANY_NIB_NUMBER_FILLED,
    // eslint-disable-next-line camelcase
    { number_type: NibTypes.NPWP }
  );

  return (
    <FormContentStyles.Container>
      <FormContentStyles.FullWidthCardContainer>
        <Card>
          <Card.Section>
            <Flex flexDirection="column" gap={space24}>
              {VerificationBanner}
              <FileUploader
                required={true}
                assetType={ASSET_TYPES.COMPANY_LEGAL_DOCUMENT}
                fieldName="verifyWithLegalDocuments.companyNPWPDoc"
                title={
                  <FormattedMessage
                    id="text-upload-company-npwp"
                    defaultMessage="Upload Company NPWP"
                  />
                }
                tooltip={
                  <VerificationPopover
                    title={<FormattedMessage {...companyNPWPTooltip.title} />}
                    items={[
                      {
                        title: (
                          <FormattedMessage
                            id="text-requirements"
                            defaultMessage="Requirements"
                          />
                        ),
                        items: [
                          <FormattedMessage
                            key="company-npwp-desc-1"
                            {...companyNPWPTooltip.description}
                          />,
                          <FormattedMessage
                            key="company-npwp-desc-2"
                            {...companyNPWPTooltip.description2}
                          />,
                        ],
                      },
                    ]}
                    images={companyNPWPTooltip.images.slice()}
                  />
                }
                subtitle={
                  <Flex alignItems="flex-start" gap={space4}>
                    <Icon
                      name="ri-verified-line"
                      width={20}
                      height={20}
                      fill={Green.B52}
                      style={{ flexShrink: 0 }}
                    />
                    <Typography variant="subtitle2" color={Green.B52}>
                      <FormattedMessage
                        id="text-original-document-get-instant-verified-verified"
                        defaultMessage="Original PDF Documents downloaded from DJP/Coretax will get verified instantly"
                        values={{ br: <br /> }}
                      />
                    </Typography>
                  </Flex>
                }
                buttonText={
                  <FormattedMessage
                    id="interactive-select-file"
                    defaultMessage="Select File"
                  />
                }
                acceptedFileType={[
                  'application/pdf',
                  'image/jpeg',
                  'image/png',
                ]}
                helperText={DefaultFileUploadHelperText}
                openTutorialModal={openTutorialModal}
                disabled={disabled}
                companyCountryCode={COUNTRY_CODES.ID}
                validateImageQuality={validateImageQuality}
              />
              <If condition={allowCompanyNameEdit}>
                <CompanyLegalNameField disabled={disabled} />
              </If>
              <If condition={showNPWPNumberField}>
                <CompanyNPWPNumberField disabled={disabled} />
              </If>
              <TextField
                fullWidth={true}
                title={
                  <FormattedMessage
                    id="text-13-digit-company-nib"
                    defaultMessage="13 Digit Company NIB - Optional"
                  />
                }
                helperText={
                  <FormattedMessage
                    id="text-providing-your-nib-speeds"
                    defaultMessage="Providing your NIB speeds up verification"
                  />
                }
                placeholder={formatMessage({
                  id: 'text-placeholder-enter-nib',
                  defaultMessage: 'Enter 13 Digits NIB',
                })}
                fieldName={companyNIBNumberFieldName}
                disabled={disabled}
                onBlur={companyNibNumberOnBlur}
              />
              <VerificationAdditionalDocumentsUploader
                fieldName="verifyWithLegalDocuments.additionalDocuments"
                disabled={disabled}
              />
            </Flex>
          </Card.Section>
        </Card>
      </FormContentStyles.FullWidthCardContainer>
      <FormContentStyles.RequiredSectionContainer>
        <Card>
          <Card.Section>
            <Flex flexDirection="column" gap="20px">
              <FormContentStyles.TutorialCard
                onClick={openTutorialModal}
                data-disabled={disabled}
              >
                <img
                  style={{
                    width: 48,
                    height: 48,
                  }}
                  src={'images/tier-verification/tutorial-card-image.jpg'}
                />
                <Flex
                  flexDirection="column"
                  gap={space4}
                  style={{
                    flex: 1,
                  }}
                >
                  <Flex
                    justifyContent="space-between"
                    style={{
                      flex: 1,
                      width: '100%',
                    }}
                  >
                    <Typography variant="body2">
                      <FormattedMessage
                        id="interactive-see-tutorial"
                        defaultMessage={'See Tutorial'}
                      />
                    </Typography>
                    <Icon
                      name={'ri-arrow-m-right-line'}
                      width={20}
                      fill={Neutral.B40}
                    />
                  </Flex>
                  <Typography variant="subtitle2" color={Neutral.B40}>
                    <FormattedMessage
                      id="interactive-learn-how-to-download"
                      defaultMessage={
                        'Learn how to download the correct NPWP PDF from government website.'
                      }
                    />
                  </Typography>
                </Flex>
              </FormContentStyles.TutorialCard>
              <Flex gap={space16} flexDirection="column">
                <Typography variant="body2">
                  <FormattedMessage
                    id="text-company-npwp-requirements"
                    defaultMessage="Company NPWP Requirements"
                  />
                </Typography>
                {requiredFieldsMessages({
                  isCompanyVerified,
                }).map(message => (
                  <FormContentStyles.RequiredSectionItem key={message.id}>
                    <Icon
                      name="ri-check"
                      width={20}
                      height={20}
                      fill={Neutral.B40}
                    />
                    <Typography
                      variant="subtitle2"
                      color={Neutral.B40}
                      style={{ maxWidth: 292 }}
                    >
                      <FormattedMessage
                        {...message}
                        values={{
                          proofOfOwnership: (...chunks: ReactNode[]) => (
                            <CTAText
                              onClick={() => {
                                if (isViewOnlyMode) return;
                                setValue(
                                  'verificationMethod',
                                  VerificationMethod.PROOF_OF_OWNERSHIP,
                                  {
                                    shouldValidate: true,
                                  }
                                );
                              }}
                            >
                              {chunks}
                            </CTAText>
                          ),
                        }}
                      />
                    </Typography>
                  </FormContentStyles.RequiredSectionItem>
                ))}
              </Flex>
            </Flex>
          </Card.Section>
        </Card>
      </FormContentStyles.RequiredSectionContainer>
      <Suspense fallback={null}>
        <DocumentTutorialModal />
      </Suspense>
    </FormContentStyles.Container>
  );
}

function VerifyWithLegalDocumentsSGForm({
  VerificationBanner,
  disabled,
}: Props) {
  const { formatMessage } = useIntl();
  const isLegalDocsAutoVerifyEnabled = useIsLegalDocsAutoVerifyEnabled();
  let instantVerificationSubtitle: ReactNode | undefined;

  if (isLegalDocsAutoVerifyEnabled) {
    instantVerificationSubtitle = (
      <Flex alignItems="center" gap={space4}>
        <Icon name="ri-verified-line" width={20} height={20} fill={Green.B52} />
        <Typography variant="subtitle2" color={Green.B52}>
          <FormattedMessage
            id="text-original-acra-bizfile-get-instant-verified"
            defaultMessage="Original PDF ACRA BizFile documents can get verified instantly."
          />
        </Typography>
      </Flex>
    );
  }

  const { trackOnFieldValid } = useCompanyVerificationTracking();
  const companyUenFieldName =
    'verifyWithLegalDocuments.companyUniqueEntityNumber';
  const companyUenOnBlur = trackOnFieldValid(
    companyUenFieldName,
    VERIFICATION_COMPANY_NIB_NUMBER_FILLED,
    // eslint-disable-next-line camelcase
    { number_type: NibTypes.UEN }
  );

  return (
    <FormContentStyles.Container>
      <FormContentStyles.FullWidthCardContainer>
        <Card>
          <Card.Section>
            <Flex flexDirection="column" gap={space24}>
              {VerificationBanner}
              <FileUploader
                required={true}
                assetType={ASSET_TYPES.ACRA_BIZFILE}
                fieldName="verifyWithLegalDocuments.acraBizFile"
                title={
                  <FormattedMessage
                    id="text-upload-company-bizfile"
                    defaultMessage="Upload Company ACRA Bizfile"
                  />
                }
                buttonText={
                  <FormattedMessage
                    id="interactive-select-file"
                    defaultMessage="Select File"
                  />
                }
                acceptedFileType={[
                  'application/pdf',
                  'image/jpeg',
                  'image/png',
                ]}
                helperText={DefaultFileUploadHelperText}
                subtitle={instantVerificationSubtitle}
                disabled={disabled}
                companyCountryCode={COUNTRY_CODES.SG}
              />
              <TextField
                fullWidth={true}
                placeholder={formatMessage({
                  id: 'text-placedholder-company-uen',
                  defaultMessage: 'Enter UEN number',
                })}
                title={
                  <FormattedMessage
                    id="text-company-uen"
                    defaultMessage="Company Unique Entity Number (UEN)"
                  />
                }
                isRequired={true}
                fieldName={companyUenFieldName}
                disabled={disabled}
                onBlur={companyUenOnBlur}
              />
            </Flex>
          </Card.Section>
        </Card>
      </FormContentStyles.FullWidthCardContainer>
    </FormContentStyles.Container>
  );
}

function VerifyWithLegalDocumentsGenericForm({
  VerificationBanner,
  disabled,
  countryCode,
}: Props & { countryCode: string }) {
  const { formatMessage } = useIntl();

  const labelForCountry = countryCode
    ? LegalDocLabelByCountryCode[countryCode] ??
      LegalDocLabelByCountryCode.default
    : LegalDocLabelByCountryCode.default;

  const { trackOnFieldValid } = useCompanyVerificationTracking();
  const companyBrnNumberFieldName =
    'verifyWithLegalDocuments.companyLegalNumber';
  const companyBrnNumberOnBlur = trackOnFieldValid(
    companyBrnNumberFieldName,
    VERIFICATION_COMPANY_NIB_NUMBER_FILLED,
    // eslint-disable-next-line camelcase
    { number_type: NibTypes.BRN }
  );

  return (
    <FormContentStyles.Container>
      <FormContentStyles.FullWidthCardContainer>
        <Card>
          <Card.Section>
            <Flex flexDirection="column" gap={space24}>
              {VerificationBanner}
              <FileUploader
                required={true}
                assetType={ASSET_TYPES.COMPANY_LEGAL_DOCUMENT}
                fieldName="verifyWithLegalDocuments.companyLegalDoc"
                title={<FormattedMessage {...labelForCountry.uploadLegalDoc} />}
                buttonText={
                  <FormattedMessage
                    id="interactive-select-file"
                    defaultMessage="Select File"
                  />
                }
                acceptedFileType={[
                  'application/pdf',
                  'image/jpeg',
                  'image/png',
                ]}
                helperText={DefaultFileUploadHelperText}
                disabled={disabled}
              />
              <TextField
                fullWidth={true}
                placeholder={formatMessage(
                  labelForCountry.legalNumberPlaceholder
                )}
                title={<FormattedMessage {...labelForCountry.legalNumber} />}
                isRequired={true}
                fieldName={companyBrnNumberFieldName}
                disabled={disabled}
                onBlur={companyBrnNumberOnBlur}
              />
            </Flex>
          </Card.Section>
        </Card>
      </FormContentStyles.FullWidthCardContainer>
    </FormContentStyles.Container>
  );
}

export default {
  IDForm: VerifyWithLegalDocumentsForm,
  SGForm: VerifyWithLegalDocumentsSGForm,
  GenericForm: VerifyWithLegalDocumentsGenericForm,
};
